import React, { useEffect, useState, useCallback, useMemo } from "react";
import { toast, Toaster } from "sonner";
import DashboardShell from "../../components/layout/DashboardShell";
import { Card, Select, Button, Pill, Input, TextArea } from "../../components/common/Primitives";
import SearchableIdSelect from "../../components/common/SearchableIdSelect";
import { Pin, EyeOff, X, Handshake, TrendingUp, Bell, DoorOpen } from "lucide-react";
import api, { formatApiError } from "../../lib/api";

export default function AdminMatchOverrides() {
  const [overrides, setOverrides] = useState([]);
  const [startups, setStartups] = useState([]);
  const [investors, setInvestors] = useState([]);
  const [form, setForm] = useState({
    startup_id: "",
    investor_owner_id: "",
    action: "pin",
    note: "",
    create_deal_room: false,
    deal_room_summary: "",
    boost_points: 10,
    notify: true,
  });

  const load = useCallback(async () => {
    try {
      const [ov, ss, iv] = await Promise.all([
        api.get("/admin/matches/overrides"),
        api.get("/admin/startups"),
        api.get("/admin/investors"),
      ]);
      setOverrides(ov.data || []);
      setStartups(ss.data || []);
      setInvestors(iv.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const startupOptions = useMemo(() => startups.map((s) => ({
    id: s.startup_id,
    label: s.startup_name || s.legal_entity_name || s.founder_email || s.startup_id,
    sub: [s.sector, s.business_stage, s.city, s.founder_email].filter(Boolean).join(" · "),
  })), [startups]);
  const investorOptions = useMemo(() => investors.map((i) => ({
    id: i.owner_user_id,
    label: i.investor_name || i.firm_name || i.owner_user_id,
    sub: [i.investor_type, i.firm_name, i.city].filter(Boolean).join(" · "),
  })), [investors]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.startup_id || !form.investor_owner_id) return toast.error("Select a startup and investor.");
    if (form.action === "boost" && (!form.boost_points || form.boost_points < 1 || form.boost_points > 30)) {
      return toast.error("Boost points must be between 1 and 30.");
    }
    try {
      const payload = { ...form };
      // Only send relevant fields for the chosen action.
      if (form.action !== "boost") delete payload.boost_points;
      if (!(form.action === "pin" || form.action === "introduce")) {
        delete payload.create_deal_room;
        delete payload.deal_room_summary;
        delete payload.notify;
      }
      const { data } = await api.post("/admin/matches/override", payload);
      let msg = `${form.action.toUpperCase()} applied.`;
      if (data.deal_room?.deal_room_id) {
        msg += data.deal_room.reused ? " Reused existing deal room." : " New deal room opened.";
      }
      toast.success(msg);
      setForm({ ...form, note: "", deal_room_summary: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const clearOverride = async (row) => {
    try {
      await api.post("/admin/matches/override", {
        startup_id: row.startup_id,
        investor_owner_id: row.investor_owner_id,
        action: "clear",
      });
      toast.success("Cleared.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const roomOpener = form.action === "pin" || form.action === "introduce";

  return (
    <DashboardShell title="Match Overrides" subtitle="ADMIN · CURATION">
      <Toaster richColors position="top-right" />
      <p className="text-ink-soft text-sm mb-6 max-w-3xl">
        Curate an investor&rsquo;s deal flow. <strong>Pin</strong> forces a pair to the top (score 100).
        <strong> Boost</strong> adds 1&ndash;30 points on top of the rule-based score. <strong>Introduce</strong> pins + spins up a private deal room + emails both parties.
        <strong> Hide</strong> suppresses the pair entirely.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card testId="override-form-card">
          <form onSubmit={submit} className="space-y-4" data-testid="override-form">
            <div className="overline">Add / Update Override</div>
            <SearchableIdSelect
              label="Startup"
              value={form.startup_id}
              onChange={(id) => setForm({ ...form, startup_id: id })}
              options={startupOptions}
              placeholder="Search startups by name, sector, email…"
              testId="override-startup-select"
              emptyText="No startups match"
            />
            <SearchableIdSelect
              label="Investor"
              value={form.investor_owner_id}
              onChange={(id) => setForm({ ...form, investor_owner_id: id })}
              options={investorOptions}
              placeholder="Search investors by name, firm, type…"
              testId="override-investor-select"
              emptyText="No investors match"
            />
            <Select
              label="Action"
              value={form.action}
              onChange={(e) => setForm({ ...form, action: e.target.value })}
              testId="override-action-select"
            >
              <option value="pin">📌 Pin — surface at top (score 100)</option>
              <option value="boost">📈 Boost — add points to base score</option>
              <option value="introduce">🤝 Introduce — pin + deal room + email both</option>
              <option value="hide">🙈 Hide — suppress from investor</option>
              <option value="clear">✂ Clear existing override</option>
            </Select>

            {form.action === "boost" && (
              <div data-testid="override-boost-config">
                <label className="text-xs font-mono uppercase tracking-widest text-ink-soft mb-1 block">
                  Boost points ({form.boost_points})
                </label>
                <input
                  type="range" min="1" max="30" step="1"
                  value={form.boost_points}
                  onChange={(e) => setForm({ ...form, boost_points: parseInt(e.target.value, 10) })}
                  className="w-full accent-gold"
                  data-testid="override-boost-slider"
                />
                <div className="flex justify-between text-[10px] font-mono text-ink-faint mt-1">
                  <span>+1 subtle</span><span>+15 curated</span><span>+30 max</span>
                </div>
              </div>
            )}

            {roomOpener && (
              <div className="border border-navy/10 rounded p-3 bg-emerald-50/40 space-y-3" data-testid="override-deal-room-block">
                {form.action === "pin" && (
                  <label className="flex items-center gap-2 text-sm text-ink cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.create_deal_room}
                      onChange={(e) => setForm({ ...form, create_deal_room: e.target.checked })}
                      className="accent-emerald-600 h-4 w-4"
                      data-testid="override-create-deal-room"
                    />
                    <DoorOpen size={14} className="text-emerald-700" />
                    <span>Also open a private deal room for this pair</span>
                  </label>
                )}
                {form.action === "introduce" && (
                  <div className="text-xs text-emerald-800 flex items-center gap-2" data-testid="override-introduce-note">
                    <Handshake size={13} className="shrink-0" />
                    <span>Introduce will auto-open a deal room and email both parties. Idempotent if a room already exists.</span>
                  </div>
                )}
                {(form.action === "introduce" || form.create_deal_room) && (
                  <>
                    <label className="flex items-center gap-2 text-sm text-ink cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.notify}
                        onChange={(e) => setForm({ ...form, notify: e.target.checked })}
                        className="accent-emerald-600 h-4 w-4"
                        data-testid="override-notify"
                      />
                      <Bell size={14} className="text-emerald-700" />
                      <span>Send warm-intro emails to founder + investor</span>
                    </label>
                    <TextArea
                      label="Deal-room summary (optional — shown in intro email)"
                      rows={2}
                      value={form.deal_room_summary}
                      onChange={(e) => setForm({ ...form, deal_room_summary: e.target.value })}
                      testId="override-deal-room-summary"
                      placeholder="e.g. Sector fit — B2B SaaS at Series A"
                    />
                  </>
                )}
              </div>
            )}

            <Input
              label="Internal Note (optional)"
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              testId="override-note"
              placeholder="e.g. Founder connected via CEO"
            />
            <Button type="submit" className="w-full" data-testid="override-submit">
              Apply {form.action}
            </Button>
          </form>
        </Card>

        <Card testId="override-list-card">
          <div className="overline mb-4">Active Overrides · {overrides.length}</div>
          <div className="space-y-3 max-h-[520px] overflow-y-auto">
            {overrides.length === 0 && <div className="text-ink-faint text-sm">No overrides yet.</div>}
            {overrides.map((r) => (
              <div key={`${r.startup_id}-${r.investor_owner_id}`} className="border border-navy/10 rounded-md p-3 flex items-start justify-between gap-3" data-testid={`override-row-${r.startup_id}-${r.investor_owner_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    {r.introduce
                      ? <Pill tone="gold"><Handshake size={11} className="inline mr-1" />Introduced</Pill>
                      : r.action === "pin"
                        ? <Pill tone="gold"><Pin size={11} className="inline mr-1" />Pinned</Pill>
                        : r.action === "boost"
                          ? <Pill tone="warn"><TrendingUp size={11} className="inline mr-1" />Boost +{r.boost_points || 0}</Pill>
                          : <Pill tone="danger"><EyeOff size={11} className="inline mr-1" />Hidden</Pill>}
                    <span className="font-mono text-[10px] text-ink-muted whitespace-nowrap">{r.at?.slice(0, 16).replace("T", " ")}</span>
                  </div>
                  <div className="text-sm text-ink font-heading truncate" title={`${r.startup_name || r.startup_id} ↔ ${r.investor_name || r.investor_owner_id}`}>
                    {r.startup_name || r.startup_id}
                    <span className="text-ink-muted"> ↔ </span>
                    {r.investor_name || r.investor_owner_id}
                  </div>
                  {r.note && (
                    <div className="text-xs text-ink-muted mt-1 italic line-clamp-2 break-words" title={r.note}>
                      &ldquo;{r.note}&rdquo;
                    </div>
                  )}
                  <div className="text-[10px] font-mono text-ink-faint mt-1 truncate" title={r.by}>by {r.by}</div>
                </div>
                <button
                  onClick={() => clearOverride(r)}
                  className="p-1.5 hover:bg-rose-500/10 text-rose-700 rounded-md flex-shrink-0"
                  title="Clear override"
                  data-testid={`override-clear-${r.startup_id}-${r.investor_owner_id}`}
                >
                  <X size={16} />
                </button>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </DashboardShell>
  );
}
